import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, ROOT_NODE } from '@craftjs/core';
import clsx from 'clsx';
import { isPopupNode } from '@/utils/helperFunc';
import { useScrollNodeIntoView } from '@/hooks/useScrollNodeIntoView';
import { bgColorClasses, getNodeThemeColorClass, PAGE_NODE_NAME } from '@/utils/constants';
import IconPageSettings from '@/assets/icons/icon-page-settings.svg?react';
import type { PopupProps } from '@builder4/shared/components/Popup';

interface TabInfo {
  id: string;
  label: string;
  isOpen: boolean;
}

const isRectOverlapped = (a: DOMRect, b: DOMRect) => {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
};

export const ContextSwitcher = () => {
  const switcherRef = useRef<HTMLDivElement | null>(null);
  const [isOccluded, setIsOccluded] = useState(false);
  const scrollNodeIntoView = useScrollNodeIntoView();

  const { actions, popupTabs, activeContextId, hoveredNodeId } = useEditor((_, query) => {
    const allNodes = query.getNodes();
    const popupTabs = Object.entries(allNodes)
      .filter(([, node]) => isPopupNode(node))
      .map(([id, node]) => ({
        id,
        label: (node.data.custom?.displayName as string) || 'Popup',
        isOpen: node.data.props.isOpen === true,
      }));

    const openPopupId = popupTabs.find((popup) => popup.isOpen)?.id;

    return {
      popupTabs,
      activeContextId: openPopupId || ROOT_NODE,
      hoveredNodeId: query.getEvent('hovered').first() as string | null | undefined,
    };
  });

  const tabs: TabInfo[] = [
    {
      id: ROOT_NODE,
      label: 'Landing Page',
      isOpen: false,
    },
    ...popupTabs,
  ];

  const colorClass = bgColorClasses[getNodeThemeColorClass(PAGE_NODE_NAME)];

  const setPopupContext = useCallback(
    (targetId: string | null) => {
      popupTabs.forEach((popup) => {
        const shouldBeOpen = popup.id === targetId;
        if (popup.isOpen === shouldBeOpen) return;

        actions.setProp(popup.id, (props: PopupProps) => {
          props.isOpen = shouldBeOpen;
        });
      });

      actions.selectNode(targetId ?? ROOT_NODE);

      if (targetId) {
        scrollNodeIntoView(targetId, true);
      }
    },
    [actions, popupTabs, scrollNodeIntoView],
  );

  const handleTabClick = useCallback(
    (nodeId: string) => {
      if (nodeId === ROOT_NODE) {
        setPopupContext(null);
        return;
      }
      setPopupContext(nodeId);
    },
    [setPopupContext],
  );

  const checkOverlap = useCallback(() => {
    if (typeof document === 'undefined') return;

    const switcherEl = switcherRef.current;
    if (!switcherEl) {
      setIsOccluded(false);
      return;
    }

    const switcherRect = switcherEl.getBoundingClientRect();
    const titleNodes = document.querySelectorAll<HTMLElement>('[data-craft-node-title="true"]');
    const overlapped = Array.from(titleNodes).some((titleNode) =>
      isRectOverlapped(switcherRect, titleNode.getBoundingClientRect()),
    );
    setIsOccluded(overlapped);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const rafId = window.requestAnimationFrame(checkOverlap);

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [checkOverlap, hoveredNodeId]);

  return (
    <div
      ref={switcherRef}
      className={clsx(
        'absolute top-0 left-0 z-50 flex items-center gap-1',
        isOccluded && 'pointer-events-none opacity-0',
      )}
      style={{
        transform: 'translate(0px, calc(-100% - 8px)) scale(calc(1 / var(--canvas-scale)))',
        transformOrigin: 'bottom left',
      }}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => handleTabClick(tab.id)}
          className={clsx(
            'flex h-7 cursor-pointer items-center gap-1 rounded-md px-2 py-1 font-["Inter"] text-[8px] leading-tight font-semibold text-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.10)]',
            tab.id === activeContextId ? colorClass : 'bg-neutral-300',
          )}
        >
          <IconPageSettings className="h-4 w-4 text-white" />
          {tab.label}
        </button>
      ))}
    </div>
  );
};
